'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OutcomeCard } from '@/lib/components/Outcomes/OutcomeCard';
import { OutcomeTabs } from '@/lib/components/Outcomes/OutcomeTabs';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { MarketItem } from '@/lib/types/markets/market';
import { Button } from '@/lib/ui-kit/button';
import { Icon } from '@/lib/ui-kit/icons';
import { getValidSortedOutcomes } from '@/lib/utils/getValidSortedOutcomes';
import { preloadImages } from '@/lib/utils/preloadImage';

type OutcomesAccordionProps = {
  outcomes: MarketItem[];
  eventId: string;
  isEventConcluded?: boolean;
};

const INITIAL_VISIBLE_COUNT = 5;

export const OutcomesAccordion = ({
  outcomes,
  isEventConcluded = false,
}: OutcomesAccordionProps) => {
  const t = useTranslations('common');
  const sortedOutcomes = useMemo(() => getValidSortedOutcomes(outcomes), [outcomes]);

  // CRITICAL FIX: Use specific selector
  const setSelectedOutcomeId = useOrderFormStore.use.setSelectedOutcomeId();
  const selectedOutcome = useSelectedOutcome();
  const [revealedOutcomeId, setRevealedOutcomeId] = useState<string | undefined>();
  const [isExpanded, setIsExpanded] = useState(false);
  const isInitialMount = useRef(true);

  const hasMore = sortedOutcomes.length > INITIAL_VISIBLE_COUNT;
  const initialOutcomes = sortedOutcomes.slice(0, INITIAL_VISIBLE_COUNT);
  const extraOutcomes = sortedOutcomes.slice(INITIAL_VISIBLE_COUNT);

  useEffect(() => {
    preloadImages(
      sortedOutcomes.slice(INITIAL_VISIBLE_COUNT).map((outcome) => outcome.image),
      'low',
    );
  }, [sortedOutcomes]);

  useEffect(() => {
    if (!selectedOutcome) {
      return;
    }
    // Skip initial mount — don't auto-open a card on page load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Auto-expand if the selected outcome is hidden
    const selectedIndex = sortedOutcomes.findIndex((o) => o.id === selectedOutcome.id);
    if (selectedIndex >= INITIAL_VISIBLE_COUNT) {
      setIsExpanded(true);
    }
    // Always open the card when selection changes (e.g. from the order form)
    setRevealedOutcomeId(selectedOutcome.id);
  }, [selectedOutcome, sortedOutcomes]);

  const handleCardClick = useCallback(
    (outcomeId: string) => {
      if (revealedOutcomeId === outcomeId) {
        setRevealedOutcomeId(undefined);
        // Don't clear selectedOutcomeId when closing - keep it for OrderForm
      } else {
        setRevealedOutcomeId(outcomeId);
        setSelectedOutcomeId(outcomeId); // Sync with selectedOutcomeId
      }
    },
    [revealedOutcomeId, setSelectedOutcomeId],
  );

  // Sync revealedOutcomeId with selectedOutcomeId when accordion value changes
  const handleAccordionChange = useCallback(
    (value: string | undefined) => {
      setRevealedOutcomeId(value);
      if (value) {
        setSelectedOutcomeId(value); // Sync selectedOutcomeId when opening accordion
      }
    },
    [setSelectedOutcomeId],
  );

  const renderOutcome = (outcome: MarketItem) => (
    <AccordionPrimitive.AccordionItem
      key={outcome.slug}
      value={outcome.id}
      className="flex flex-col border-b border-v2-border last:border-b-0"
    >
      <AccordionPrimitive.AccordionTrigger className="transition-all" asChild>
        <OutcomeCard
          outcome={outcome}
          isEventConcluded={isEventConcluded}
          showBadges={outcomes.length > 1}
          onClick={() => {
            handleCardClick(outcome.id);
          }}
        />
      </AccordionPrimitive.AccordionTrigger>
      <AccordionPrimitive.AccordionContent
        data-slot="accordion-content"
        className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down flex flex-col
          overflow-hidden"
      >
        <OutcomeTabs
          className="pt-2"
          outcome={outcome}
          showChartTab={outcomes.length > 1}
          showPositionsTabs={true}
          isEventConcluded={isEventConcluded}
        />
      </AccordionPrimitive.AccordionContent>
    </AccordionPrimitive.AccordionItem>
  );

  return (
    <div>
      <AccordionPrimitive.Accordion
        id="outcomes-section"
        type="single"
        collapsible
        className="scroll-mt-[150px]"
        value={revealedOutcomeId}
        onValueChange={handleAccordionChange}
      >
        {initialOutcomes.map(renderOutcome)}

        {hasMore && (
          <Button
            variant="text"
            size="sm"
            className="text-muted-foreground mt-1 gap-1 px-4"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? t('showLess') : t('showMore')}
            <Icon
              name="ChevronDown"
              size={16}
              className={isExpanded ? 'rotate-180 transition-transform' : 'transition-transform'}
            />
          </Button>
        )}

        {isExpanded && extraOutcomes.map(renderOutcome)}
      </AccordionPrimitive.Accordion>
    </div>
  );
};
